import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import branding from '../branding.json';

export const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const { darkMode } = useTheme();
  const [exporting, setExporting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const legalBranding = (branding as any).legal;
  const contactEmail = legalBranding?.complianceContact || branding.companyInfo.email;

  if (!user) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const handleDataExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/auth/users/me/data-export', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'my-data-export.json');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Could surface a toast here in future
    }
    setExporting(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.delete('/auth/users/me');
      await logout();
    } catch (err: any) {
      setDeleteError(
        err.response?.data?.detail || 'Failed to request account deletion.'
      );
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <h1 className={`text-2xl font-bold mb-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
        Your Profile
      </h1>

      {/* Avatar + name card */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 mb-6`}>
        <div className="flex items-center space-x-6">
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.full_name || user.username || 'User'}
              className="w-24 h-24 rounded-full object-cover border-4 border-blue-500"
            />
          ) : (
            <div
              className={`w-24 h-24 rounded-full flex items-center justify-center text-2xl font-medium ${
                darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-800'
              }`}
            >
              {user.full_name
                ?.split(' ')
                .map((n: string) => n[0])
                .join('')
                .toUpperCase()
                .substring(0, 2) ||
                user.username?.substring(0, 2).toUpperCase() ||
                user.email?.substring(0, 2).toUpperCase()}
            </div>
          )}
          <div>
            <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {user.full_name || user.username || user.email.split('@')[0]}
            </h2>
            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
              {user.email}
            </p>
            {user.auth_provider && (
              <p className={`mt-1 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Signed in with {user.auth_provider}
              </p>
            )}
            {user.is_superuser && (
              <span className="mt-2 inline-block px-2 py-1 text-xs font-semibold text-white bg-purple-600 rounded-full">
                Administrator
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Account info */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6 mb-6`}>
        <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Account Information
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Username</p>
            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {user.username || 'Not set'}
            </p>
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Email</p>
            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {user.email}
            </p>
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Account Type</p>
            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {user.is_superuser ? 'Administrator' : 'Standard User'}
            </p>
          </div>
          <div>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Authentication Method</p>
            <p className={`font-medium ${darkMode ? 'text-white' : 'text-gray-800'}`}>
              {user.auth_provider
                ? `${user.auth_provider} (OIDC)`
                : 'Local Authentication'}
            </p>
          </div>
        </div>
      </div>

      {/* Data & Privacy */}
      <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-6`}>
        <h3 className={`text-lg font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          Data &amp; Privacy
        </h3>
        <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          You have the right to export or delete your personal data at any time.
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <button
              onClick={handleDataExport}
              disabled={exporting}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${
                darkMode
                  ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {exporting ? 'Preparing export...' : 'Download My Data'}
            </button>
            {/* Addendum A: token usage gap note */}
            <p className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              Export includes account info, consent history, identities, and audit log.
              AI token usage is not currently included as it is not linked to your account.
            </p>
          </div>

          {!user.is_superuser && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
            >
              Delete My Account
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
          <div
            className={`${
              darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
            } p-8 rounded-lg shadow-xl max-w-md w-full mx-4`}
          >
            <h2 className="text-xl font-bold mb-2 text-red-600">
              Delete Account
            </h2>
            <p className={`text-sm mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Your account will be disabled immediately and permanently deleted
              after a <strong>30-day grace period</strong>. You may cancel by
              logging back in within that window.
            </p>
            <p className={`text-sm mb-4 font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
              This action cannot be undone after the grace period.
            </p>
            {/* Addendum C: contact guidance */}
            <p className={`text-sm mb-6 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              To cancel during the grace period, log back in or contact us at{' '}
              <a href={`mailto:${contactEmail}`} className="underline">
                {contactEmail}
              </a>
            </p>

            {deleteError && (
              <p className="text-sm text-red-500 mb-4">{deleteError}</p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteError(null);
                }}
                disabled={deleting}
                className={`flex-1 px-4 py-2 rounded-md text-sm font-medium border ${
                  darkMode
                    ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting}
                className="flex-1 px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700"
              >
                {deleting ? 'Processing...' : 'Yes, delete my account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
